import { Button } from '@ordo/ui/button'
import { Input } from '@ordo/ui/input'
import { useAsyncLoader } from 'hooks/useAsyncLoader'
import { Plus, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { Contact } from 'types/contacts'
import { formatPhone } from 'utils/phone'

const SEARCH_DEBOUNCE_MS = 200
const SEARCH_PAGE_SIZE = 50

function contactDisplayName(contact: Contact): string {
  return [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.email || '(unnamed)'
}

interface StaticContactPickerProps {
  selectedContacts: Contact[]
  onAdd: (contact: Contact) => void
  onRemove: (contactId: string) => void
}

/**
 * Static-list member picker with server-side search. Hits /api/contacts?q=…
 * with debounce so it scales past the page-size cap that broke the previous
 * client-side `useAllContacts` filter. Results are filtered locally to
 * exclude already-selected ids.
 */
export function StaticContactPicker({ selectedContacts, onAdd, onRemove }: StaticContactPickerProps) {
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [results, setResults] = useState<Contact[]>([])
  const [searching, setSearching] = useState(false)
  const fetchCountRef = useRef(0)

  const loadContacts = useAsyncLoader<Contact>('/api/contacts', undefined, SEARCH_PAGE_SIZE)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    if (debounced.length === 0) {
      setResults([])
      return
    }
    const fetchId = ++fetchCountRef.current
    setSearching(true)
    loadContacts(debounced)
      .then((items) => {
        if (fetchId === fetchCountRef.current) {
          setResults(items)
        }
      })
      .finally(() => {
        if (fetchId === fetchCountRef.current) {
          setSearching(false)
        }
      })
  }, [debounced, loadContacts])

  const selectedIds = useMemo(() => new Set(selectedContacts.map((c) => c.id)), [selectedContacts])
  const availableResults = useMemo(() => results.filter((c) => !selectedIds.has(c.id)), [results, selectedIds])

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm font-medium text-muted-foreground">Members ({selectedContacts.length})</label>
        {selectedContacts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No contacts added yet.</p>
        ) : (
          <div className="space-y-1">
            {selectedContacts.map((contact) => (
              <div
                key={contact.id}
                className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                <div className="text-sm">
                  <span>{contactDisplayName(contact)}</span>
                  {contact.email && <span className="ml-2 text-muted-foreground">{contact.email}</span>}
                  {contact.phoneNumber && (
                    <span className="ml-2 text-muted-foreground">{formatPhone(contact.phoneNumber)}</span>
                  )}
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => onRemove(contact.id)}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="static-contact-search" className="text-sm font-medium text-muted-foreground">
          Add Contacts
        </label>
        <Input
          id="static-contact-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search contacts..."
        />
        {search.trim().length > 0 && (
          <div className="max-h-50 overflow-y-auto rounded-md border border-border">
            {searching && availableResults.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">Searching…</p>
            ) : availableResults.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">No matching contacts.</p>
            ) : (
              availableResults.map((contact) => (
                <button
                  key={contact.id}
                  type="button"
                  onClick={() => {
                    onAdd(contact)
                    setSearch('')
                  }}
                  className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted/50">
                  <div>
                    <span>{contactDisplayName(contact)}</span>
                    {contact.email && <span className="ml-2 text-muted-foreground">{contact.email}</span>}
                  </div>
                  <Plus className="h-4 w-4 text-muted-foreground" />
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  )
}
